import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import defaultContent from "../../../../../content/downlineProfile";
import { IconMetricsGraph } from "../../../../../common/icons/IconMetricsGraph/IconMetricsGraph";
import { IconPlainTable } from "../../../../../common/icons/IconPlainTable/IconPlainTable";
import { IconGear } from "../../../../../common/icons/IconGear/IconGear";
import "./MetricsToolbar.scss";
import { KpiSelector } from "../../../../../common/components/KpiSelector/KpiSelector";
import { RootState, useAppDispatch, useAppSelector } from "../../../../../store";
import { useUpdateDataviewKpisMutation } from "../../../../../services/Dataview/DataviewAPI";
import { setIsApplyingKpis, setSelectedKpis, setViewMode } from "../../../../../reducers/downlineProfile";
import { aboProfileAPI, useGetSeriesDataviewQuery } from "../../../../../services/AboProfile/AboProfileAPI";
import { createPortal } from "react-dom";
import { marketCode, storedLocale } from "../../../../Authentication/util";
import {
  trackCustomizeKpiModalOpen,
  trackGraphView,
  trackKpiSelectionAdded,
  trackKpiSelectionRemoved,
  trackKpiSelectorClose,
  trackKpiSelectorOpen,
  trackKpiSelectorSaveSelection,
  trackTableView,
} from "../../../PlatinumProfile/downlineProfileTealium";

const getScrollParent = (element: HTMLElement | null): HTMLElement | Window => {
  let node = element?.parentElement ?? null;
  while (node) {
    const { overflowY } = window.getComputedStyle(node);
    if ((overflowY === "auto" || overflowY === "scroll") && node.scrollHeight > node.clientHeight) {
      return node;
    }
    node = node.parentElement;
  }
  return window;
};

const MetricsToolbar = () => {
  const { t } = useTranslation("downlineProfile");
  const dispatch = useAppDispatch();
  const [showKpiSelector, setShowKpiSelector] = useState(false);
  const [selectorPos, setSelectorPos] = useState<{ top: number; right?: number; left?: number } | null>(null);
  const kpiBtnRef = useRef<HTMLButtonElement>(null);
  const kpiSelectorScrollObserverRef = useRef<ResizeObserver | null>(null);
  const downlineProfileSelectedKpis = useAppSelector((s) => s.downlineProfile.selectedKpis);
  const downlineProfileAvailableKpis = useAppSelector((s) => s.downlineProfile.availableKpis);
  const isApplyingKpis = useAppSelector((s) => s.downlineProfile.isApplyingKpis);
  const view = useAppSelector((s: RootState) => s.downlineProfile.viewMode);
  const [updateDataviewKpis] = useUpdateDataviewKpisMutation();
  const downlineProfileAffAbo = useAppSelector((state: RootState) => state.downlineProfile.affAbo);
  const [aff = "", abo = ""] = downlineProfileAffAbo.split("-");
  const { isFetching, isError } = useGetSeriesDataviewQuery(
    {
      aff,
      abo,
      locale: storedLocale(),
      dataViewID: "downlineProfile",
      market: marketCode(),
    },
    { skip: !aff || !abo },
  );
  useEffect(() => {
    if (!showKpiSelector) {
      setSelectorPos(null);
      return;
    }

    if (!kpiBtnRef.current) return;
    const compute = () => {
      const rect = kpiBtnRef.current!.getBoundingClientRect();
      if (window.innerWidth <= 1023) {
        setSelectorPos({ top: rect.bottom + 12, left: rect.left });
      } else {
        setSelectorPos({ top: rect.bottom + 12, right: window.innerWidth - rect.right });
      }
    };

    const handleClickOutside = (event: MouseEvent) => {
      // Find your portal container element (you can add a ref to it)
      const portalElement = document.querySelector(".metrics-toolbar__kpi-selector-portal");
      const mobilePortalElement = document.querySelector(".kpi-tracking-filter-modal");
      // If the click was NOT on the button and NOT inside the dropdown portal, close it
      const targetNode = event.target as Node;
      if (
        kpiBtnRef.current &&
        !kpiBtnRef.current.contains(targetNode) &&
        portalElement &&
        !portalElement.contains(targetNode) &&
        (!mobilePortalElement || !mobilePortalElement.contains(targetNode))
      ) {
        setShowKpiSelector(false);
        trackKpiSelectorClose();
      }
    };
    compute();
    window.addEventListener("resize", compute);
    window.addEventListener("scroll", compute, { capture: true, passive: true });
    window.addEventListener("click", handleClickOutside, { capture: true });
    return () => {
      window.removeEventListener("resize", compute);
      window.removeEventListener("scroll", compute, { capture: true });
      window.removeEventListener("click", handleClickOutside, { capture: true });
    };
  }, [showKpiSelector]);

  const setKpiModalNode = useCallback((node: HTMLDivElement | null) => {
    kpiSelectorScrollObserverRef.current?.disconnect();
    kpiSelectorScrollObserverRef.current = null;

    if (!node) return;

    const visibleModal = node.querySelector<HTMLElement>(".kpi-selector");

    if (!visibleModal) return;

    const viewportPadding = 16;

    const maybeScrollIntoView = () => {
      const rect = visibleModal.getBoundingClientRect();
      const overflowsBottom = rect.bottom > window.innerHeight - viewportPadding;
      if (!overflowsBottom) return;

      const scrollDelta = rect.top - viewportPadding;
      if (scrollDelta <= 0) return;

      getScrollParent(kpiBtnRef.current).scrollBy({ top: scrollDelta, behavior: "smooth" });
    };

    const observer = new ResizeObserver(maybeScrollIntoView);
    observer.observe(visibleModal);
    kpiSelectorScrollObserverRef.current = observer;
    maybeScrollIntoView();
  }, []);

  return (
    <div className="metrics-toolbar" data-testid="metrics-toolbar">
      <div className="metrics-toolbar__view-toggle">
        <button
          type="button"
          className={`metrics-toolbar__view-btn metrics-toolbar__view-btn--left${view === "graphView" ? " metrics-toolbar__view-btn--active" : ""}`}
          data-testid="metrics-toolbar-graph-btn"
          aria-label={t("toolbar.graphView", defaultContent.toolbar.graphView) as string}
          aria-pressed={view === "graphView"}
          onClick={() => {
            dispatch(setViewMode("graphView"));
            trackGraphView();
          }}
          disabled={view === "graphView"}
        >
          <IconMetricsGraph />
        </button>
        <button
          type="button"
          className={`metrics-toolbar__view-btn metrics-toolbar__view-btn--right${view === "tableView" ? " metrics-toolbar__view-btn--active" : ""}`}
          data-testid="metrics-toolbar-raw-btn"
          aria-label={t("toolbar.rawView", defaultContent.toolbar.rawView) as string}
          aria-pressed={view === "tableView"}
          onClick={() => {
            dispatch(setViewMode("tableView"));
            trackTableView();
          }}
          disabled={view === "tableView"}
        >
          <IconPlainTable />
        </button>
      </div>

      <div className="metrics-toolbar__kpi-btn-wrapper">
        <button
          ref={kpiBtnRef}
          type="button"
          className={`metrics-toolbar__kpi-btn ${showKpiSelector && "metrics-toolbar__kpi-btn--active"}`}
          data-testid="metrics-toolbar-kpi-btn"
          aria-label={t("toolbar.selectKpi", defaultContent.toolbar.selectKpi) as string}
          onClick={() => {
            setShowKpiSelector((prev) => !prev);
            if (!showKpiSelector) {
              trackCustomizeKpiModalOpen();
            } else {
              trackKpiSelectorOpen();
            }
          }}
          disabled={isFetching || isError || isApplyingKpis}
        >
          <IconGear noBorder />
        </button>
        {showKpiSelector &&
          selectorPos &&
          createPortal(
            <>
              <div
                className="metrics-toolbar__kpi-selector-portal"
                style={{
                  position: "fixed",
                  top: selectorPos.top,
                  left: selectorPos.left,
                  right: selectorPos.right,
                  zIndex: 1001,
                }}
                ref={setKpiModalNode}
              >
                <KpiSelector
                  show={showKpiSelector}
                  selectedKpis={downlineProfileSelectedKpis}
                  availableKpis={downlineProfileAvailableKpis}
                  minSelectionsAllowed={1}
                  maxSelectionsAllowed={8}
                  onSubmitFn={async (selectedKpis) => {
                    const added = selectedKpis.filter((kpi) => !downlineProfileSelectedKpis.includes(kpi));
                    const removed = downlineProfileSelectedKpis.filter((kpi) => !selectedKpis.includes(kpi));
                    added.forEach((kpi) => trackKpiSelectionAdded(kpi));
                    removed.forEach((kpi) => trackKpiSelectionRemoved(kpi));
                    setShowKpiSelector(false);
                    dispatch(setIsApplyingKpis(true));
                    dispatch(setSelectedKpis(selectedKpis));
                    await updateDataviewKpis({
                      dataviewId: "downlineProfile",
                      selectedKpis: selectedKpis.join(","),
                    }).unwrap();
                    dispatch(aboProfileAPI.util.invalidateTags(["SeriesDataview"]));
                    dispatch(setIsApplyingKpis(false));
                    trackKpiSelectorSaveSelection();
                  }}
                  onCloseCallbackFn={() => {
                    setShowKpiSelector(false);
                    trackKpiSelectorClose();
                  }}
                  disableScrollLock={true}
                  textOverride={{ groupPV: t("performanceMetrics.groupPV", defaultContent.performanceMetrics.groupPV) }}
                />
              </div>
            </>,
            document.body,
          )}
      </div>
    </div>
  );
};

export default MetricsToolbar;
